import { Typography, Box, IconButton } from '@mui/material';
import { useState, useEffect, useMemo, useContext, FC } from 'react';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { getPunchlistTodos, getPunchlistTodoListPdf } from '../../../../fetch';
import {
  IResponse,
  IPunchlistTodo,
  IDateRange,
  IVendorContact,
  INeighborhood,
  IDomainUser,
  IDropdownResponse
} from '../../../../models';
import { Loader, Pagination, Button } from '../../../../components';
import { PunchlistFilters } from '../PunchlistFilters';
import { TodosAccordion } from './TodosAccordion';
import { TodoDetailModal } from '../punchlist-item/TodoDetailModal';
import { VendorPageContext, UserContext } from '../../../../context';
import { UnfoldLess, UnfoldMore, Add, CloudDownload } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { downloadFile } from '../../../../helpers';

interface ITodosList {
  route?: string;
  showTodoModal: (val?: boolean) => void;
  isTodoModalOpen: boolean;
  showArchived?: boolean;
  excludeEmptyForVendors: boolean;
}

// Component shared between /production/punchlists/todos and /vendors/punchlists
// Renders list with filters for punchlist to-do items
export const TodosList: FC<ITodosList> = ({
  route,
  isTodoModalOpen,
  showTodoModal,
  showArchived = false,
  excludeEmptyForVendors = false
}) => {
  // state
  const { isAdmin } = useContext(UserContext);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
  const [territoryId, setTerritoryId] = useState<string | number>('');
  const [neighborhoodId, setNeighborhoodId] = useState<string | number>('');
  const [selectedLotNumber, setSelectedLotNumber] = useState<string>('');
  const [vendorId, setVendorId] = useState<string | number>('');
  const [vendorContact, setVendorContact] = useState<string>('');
  const [vendors, setVendors] = useState<IDropdownResponse[] | null>(null);
  const [neighborhoods, setNeighborhoods] = useState<INeighborhood[] | null>(null);
  const [vendorContacts, setVendorContacts] = useState<IVendorContact[] | null>(null);
  const [internalUsers, setInternalUsers] = useState<IDomainUser[] | null>(null);
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [selectedAssignee, setSelectedAssignee] = useState<IDomainUser | null>(null);
  const [filters, setFilters] = useState<{
    territoryId?: string | number;
    neighborhoodId?: number | string;
    lotNumber?: string;
    vendorId?: number | string;
    startDate?: string;
    endDate?: string;
    assignedTo?: string;
  } | null>({});
  const [page, setPage] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [selectedDateRange, setSelectedDateRange] = useState<IDateRange>({
    startDate: null,
    endDate: null,
    key: 'selection',
    inputValue: ''
  });
  // used to change items on the fly from completed/not-completed
  const [todoItems, setTodoItems] = useState<IPunchlistTodo[]>([]);
  const [isAccordionsExpanded, setAccordionsExpanded] = useState<boolean>(true);
  // context
  const { enqueueSnackbar } = useSnackbar();
  const { loadingVendors, vendorName, handleVendorSelect, vendorSageId } =
    useContext(VendorPageContext);
  // queries

  const {
    isLoading: isLoadingPunchlists,
    data: todosData,
    refetch: refetchPunchlistTodos
  } = useQuery<IResponse<IPunchlistTodo[]>, Error>(
    ['punchlist-todos', page, perPage, filters, vendors, vendorName, vendorContacts, showArchived],
    () => {
      return getPunchlistTodos({
        page: page + 1,
        perPage,
        ...filters,
        vendorSageId,
        includeArchived: showArchived,
        excludeEmptyForVendorView: excludeEmptyForVendors
      });
    },
    {
      // wait before these vars are not null before we call the /todos enpoint so we don't double load
      enabled:
        !!neighborhoods && (!!vendors || !!vendorName || !!vendorContacts || !!internalUsers),
      onSuccess: (d: any) => {
        const data: IResponse<IPunchlistTodo[]> = d;
        setTodoItems(data.records);
        setRecordCount(data.totalRecordCount);
        return d;
      },
      onError: (e: any) => {
        setTodoItems([]);
        enqueueSnackbar(e?.Detail || `Error loading punchlist recent activity.`, {
          variant: 'error'
        });
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  // initialize the data
  const todos = useMemo(() => todosData?.records ?? [], [todosData]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const paramVendorId = searchParams.get('vendorId');
    const paramAssignedTo = searchParams.get('assignedTo');
    const paramNeighborhoodId = searchParams.get('neighborhoodId');
    if ((paramAssignedTo || paramVendorId || paramNeighborhoodId) && vendors && neighborhoods) {
      const selectedVendor = vendors?.find(v => +v.value === Number(paramVendorId));
      const selectedContact = vendorContacts?.find(v => v.email === paramAssignedTo);
      const selectedInternaluser = internalUsers?.find(
        user => user.accountName === paramAssignedTo
      );
      const selectedNeighborhood = neighborhoods?.find(
        n => n.neighborhoodId === Number(paramNeighborhoodId)
      );
      if (selectedVendor) {
        setVendorId(selectedVendor.value);
        if (handleVendorSelect) {
          handleVendorSelect(selectedVendor as any);
        } else {
          setFilters({
            ...filters,
            vendorId: selectedVendor.value
          });
        }
      }
      if (selectedContact) {
        setVendorContact(selectedContact.email);
        setFilters({
          ...filters,
          assignedTo: selectedContact.email
        });
      }
      if (selectedInternaluser) {
        setSelectedAssignee(selectedInternaluser);
        setFilters({
          ...filters,
          assignedTo: paramAssignedTo
        });
      }
      if (selectedNeighborhood) {
        setNeighborhoodId(selectedNeighborhood.neighborhoodId);
        setFilters({
          ...filters,
          neighborhoodId: selectedNeighborhood.neighborhoodId
        });
      }
      setHasAppliedFilters(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendors, vendorContacts, neighborhoods, internalUsers]);

  const isLoading = useMemo(
    () =>
      isLoadingPunchlists || loadingVendors || !neighborhoods || (vendorName ? false : !vendors),
    [isLoadingPunchlists, neighborhoods, vendors, loadingVendors, vendorName]
  );
  return (
    <>
      <PunchlistFilters
        isLoading={isLoading}
        selectedTerritoryId={territoryId}
        setSelectedTerritoryId={setTerritoryId}
        selectedNeighborhoodId={neighborhoodId}
        setVendors={setVendors}
        setNeighborhoods={setNeighborhoods}
        setVendorContacts={setVendorContacts}
        selectedVendorId={vendorId}
        setVendorContact={setVendorContact}
        vendorContact={vendorContact}
        setSelectedVendorId={setVendorId}
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={setSelectedDateRange}
        setSelectedNeighborhoodId={setNeighborhoodId}
        setHasAppliedFilters={setHasAppliedFilters}
        hasAppliedFilters={hasAppliedFilters}
        setAssigneeOptions={setInternalUsers}
        setSelectedAssignee={setSelectedAssignee}
        selectedAssignee={selectedAssignee}
        selectedLotNumber={selectedLotNumber}
        setSelectedLotNumber={setSelectedLotNumber}
        hideVendorFilter={!!vendorName || loadingVendors}
        applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
          if (clearFilters) {
            setFilters({
              territoryId: '',
              neighborhoodId: '',
              vendorId: undefined,
              assignedTo: '',
              startDate: undefined,
              endDate: undefined,
              lotNumber: ''
            });
          } else {
            setPage(0);
            setFilters({
              ...filters,
              territoryId: territoryId || undefined,
              neighborhoodId: neighborhoodId || undefined,
              vendorId: vendorId || undefined,
              assignedTo: vendorContact || selectedAssignee?.accountName || undefined,
              startDate: selectedDateRange?.startDate?.toISOString() || undefined,
              endDate: selectedDateRange?.endDate?.toISOString() || undefined,
              lotNumber: selectedLotNumber || undefined
            });
          }
        }}
      />
      {isLoading && <Loader position="centered" type="inline" />}
      {!isLoading && todos?.length === 0 && (
        <Box display="flex" justifyContent="center" marginTop="1rem">
          <Typography variant="body1" gutterBottom>
            No todos found.
          </Typography>
        </Box>
      )}
      {!isLoading && todos?.length > 0 && (
        <Box display="flex" justifyContent="space-between">
          <LoadingButton
            loading={isDownloadingPdf}
            loadingPosition="start"
            color="secondary"
            variant="contained"
            disabled={isDownloadingPdf}
            onClick={async () => {
              try {
                setIsDownloadingPdf(true);
                const blob = await getPunchlistTodoListPdf({
                  territoryId: territoryId || undefined,
                  neighborhoodId: neighborhoodId || undefined,
                  lotNumber: selectedLotNumber || undefined,
                  vendorId: vendorId || undefined,
                  assignedTo: vendorContact || selectedAssignee?.accountName || undefined,
                  startDate: selectedDateRange?.startDate?.toISOString() || undefined,
                  endDate: selectedDateRange?.endDate?.toISOString() || undefined,
                  vendorSageId: vendorSageId || undefined,
                  includeArchived: showArchived
                });

                if (blob) {
                  downloadFile(blob, 'punchlist_todos.pdf');
                } else {
                  throw new Error('No PDF data received');
                }
              } catch (error) {
                enqueueSnackbar(error?.message ?? `Error downloading PDF file`, {
                  variant: 'error'
                });
              } finally {
                setIsDownloadingPdf(false);
              }
            }}
            startIcon={<CloudDownload />}
          >
            PDF
          </LoadingButton>
          <IconButton
            onClick={() => setAccordionsExpanded(!isAccordionsExpanded)}
            title={`${isAccordionsExpanded ? 'Collapse' : 'Expand'} all punchlists`}
          >
            {isAccordionsExpanded ? <UnfoldLess /> : <UnfoldMore />}
          </IconButton>
        </Box>
      )}
      {!isLoading &&
        todoItems.map((item, index) => {
          return (
            <TodosAccordion
              route={route}
              todosItem={item}
              punchlistIndex={index}
              key={`${index}`}
              setTodoItems={setTodoItems}
              todoItems={todoItems}
              refetchData={refetchPunchlistTodos}
              isAllAccodionsExpanded={isAccordionsExpanded}
            />
          );
        })}
      {!isLoading && todos?.length > 0 && (
        <>
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
          {isAdmin && (
            <Box display="flex" alignItems="center" justifyContent="flex-end">
              <Button
                id="add-todo"
                onClick={() => {
                  showTodoModal(true);
                }}
                variant="contained"
                color="primary"
                startIcon={<Add />}
              >
                Add Todo
              </Button>
            </Box>
          )}
        </>
      )}
      <TodoDetailModal
        open={isTodoModalOpen}
        handleClose={(shouldRefresh?: boolean, shouldClose = true) => {
          if (shouldRefresh) {
            refetchPunchlistTodos();
          }
          if (shouldClose) showTodoModal(false);
        }}
      />
    </>
  );
};
